import { apiClient } from "@/shared/services/api";

export interface ReportExportFile {
  blob: Blob;
  fileName: string;
}

export type ReportsQueryFilters = {
  pageNumber?: string;
  pageSize?: string;
  groupPageNumber?: string;
  groupPageSize?: string;
  asOf?: string;
  startDate?: string;
  endDate?: string;
  granularity?: "day" | "week" | "month";
  groupBy?: "input" | "department" | "inputType" | "greenhouse" | "field" | "variety" | "requisition" | "day";
  period1Start?: string;
  period1End?: string;
  period2Start?: string;
  period2End?: string;
  orderId?: string;
  greenhouseId?: string;
  sowingInstructionId?: string;
  departmentId?: string;
  fieldId?: string;
  inputTypeId?: string;
  inputId?: string;
  requisitionId?: string;
  sourceReferenceType?: string;
  sourceReferenceNumber?: string;
  variety?: string;
  supplierId?: string;
  customerId?: string;
  cropTypeId?: string;
};

export type ReportType =
  | "production-costs"
  | "input-costs"
  | "material-usage"
  | "profitability-summary"
  | "cost-comparison"
  | "order-profitability"
  | "payment-summary"
  | "revenue-summary"
  | "revenue-by-period"
  | "revenue-by-customer"
  | "revenue-by-product"
  | "revenue-ar-summary";

export interface OrderProfitabilityReportResponse {
  [key: string]: unknown;
}

export interface ActivityTypeCostChange {
  activityType: string;
  activityTypeName: string;
  period1Cost: number;
  period2Cost: number;
  change: number;
  changePercent: number;
}

export interface CostComparisonReportResponse {
  period1Start: string;
  period1End: string;
  period2Start: string;
  period2End: string;
  period1ProductionCosts: number;
  period2ProductionCosts: number;
  productionCostChange: number;
  productionCostChangePercent: number;
  period1InputCosts: number;
  period2InputCosts: number;
  inputCostChange: number;
  inputCostChangePercent: number;
  activityTypeChanges: ActivityTypeCostChange[];
}

export interface ProductionCostByActivityType {
  activityType: string;
  activityTypeName: string;
  laborCost: number;
  materialCost: number;
  totalCost: number;
  activityCount: number;
}

export interface ProductionCostByGreenhouse {
  greenhouseId: string;
  greenhouseName: string;
  totalLaborCost: number;
  totalMaterialCost: number;
  totalCost: number;
  activityCount: number;
}

export interface TopCostActivity {
  activityId: string;
  activityDate: string;
  activityType: string;
  activityTypeName: string;
  greenhouseName: string;
  description: string;
  laborCost: number;
  materialCost: number;
  totalCost: number;
  performedBy: string;
}

export interface ProductionCostsReportResponse {
  startDate: string;
  endDate: string;
  totalLaborCost: number;
  totalMaterialCost: number;
  totalActivityCost: number;
  activityCount: number;
  costsByActivityType: ProductionCostByActivityType[];
  costsByGreenhouse: ProductionCostByGreenhouse[];
  topCostActivities: TopCostActivity[];
}

export interface InputCostByType {
  inputTypeId: string;
  inputTypeName: string;
  totalPurchaseCost: number;
  totalUsageCost: number;
  averageUnitCost: number;
  purchaseCount: number;
}

export interface InputCostBySupplier {
  supplierId: string;
  supplierName: string;
  totalPurchaseCost: number;
  averageUnitCost: number;
  purchaseCount: number;
}

export interface TopPurchase {
  stockMovementId: string;
  movementDate: string;
  inputName: string;
  supplierName: string;
  quantity: number;
  unit: string;
  unitCost: number;
  totalCost: number;
  batchNumber: string;
}

export interface InputCostsReportResponse {
  startDate: string;
  endDate: string;
  totalPurchaseCost: number;
  totalUsageCost: number;
  purchaseCount: number;
  usageCount: number;
  costsByInputType: InputCostByType[];
  costsBySupplier: InputCostBySupplier[];
  topPurchases: TopPurchase[];
}

export interface MaterialUsageReportRow {
  stockMovementId: string;
  movementDate: string;
  movementType: string;
  quantity: number;
  signedQuantity: number;
  unitCost: number;
  signedCost: number;
  inputId?: string | null;
  inputName: string;
  inputCode: string;
  inputTypeId?: string | null;
  inputTypeName: string;
  cropTypeId?: string | null;
  variety?: string | null;
  departmentId?: string | null;
  departmentName: string;
  requisitionId?: string | null;
  requisitionNumber?: string | null;
  requisitionItemId?: string | null;
  sowingInstructionId?: string | null;
  greenhouseId?: string | null;
  farmId?: string | null;
  fieldId?: string | null;
  inputBatchId?: string | null;
  batchNumber?: string | null;
  activityType?: string | null;
  purpose?: string | null;
  recordedByUserName?: string | null;
  recordedAt?: string | null;
  sourceReferenceType?: string | null;
  sourceReferenceNumber?: string | null;
  referenceNumber?: string | null;
  reason?: string | null;
  notes?: string | null;
}

export interface MaterialUsageReportGroup {
  groupKey: string;
  groupLabel: string;
  totalIssuedQuantity: number;
  totalReturnedQuantity: number;
  netUsedQuantity: number;
  netUsageCost: number;
  movementCount: number;
}

export interface MaterialUsageReportResponse {
  startDate?: string | null;
  endDate?: string | null;
  totalIssuedQuantity: number;
  totalReturnedQuantity: number;
  netUsedQuantity: number;
  netUsageCost: number;
  issueCount: number;
  returnCount: number;
  rows: MaterialUsageReportRow[];
  groups: MaterialUsageReportGroup[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  groupPageNumber: number;
  groupPageSize: number;
  totalGroupCount: number;
}

export interface MonthlyProfitability {
  year: number;
  month: number;
  monthName: string;
  revenue: number;
  costs: number;
  profit: number;
  profitMarginPercent: number;
  orderCount: number;
}

export interface VarietyProfitability {
  variety: string;
  revenue: number;
  costs: number;
  profit: number;
  profitMarginPercent: number;
  orderCount: number;
}

export interface ProfitByCropType {
  cropTypeId: string;
  cropTypeName: string;
  revenue: number;
  costs: number;
  profit: number;
  profitMarginPercent: number;
  orderCount: number;
  varieties: VarietyProfitability[];
}

export interface ProfitabilitySummaryReportResponse {
  startDate: string;
  endDate: string;
  totalRevenue: number;
  totalProductionCosts: number;
  totalInputCosts: number;
  totalCosts: number;
  grossProfit: number;
  profitMarginPercent: number;
  orderCount: number;
  completedOrderCount: number;
  monthlyBreakdown: MonthlyProfitability[];
  profitByCropType: ProfitByCropType[];
}

export interface PaymentSummaryReportResponse {
  totalReceived: number;
  totalRefunded: number;
  netAmount: number;
  paymentCount: number;
  refundCount: number;
  unreconciledCount: number;
}

export interface RevenueSummaryReportResponse {
  from: string;
  to: string;
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  totalOverdue: number;
  totalVoided: number;
  vatCollected: number;
  netRevenue: number;
  invoiceCount: number;
  paidCount: number;
  overdueCount: number;
  voidedCount: number;
}

export interface RevenueByCustomerItem {
  customerId: string;
  customerName: string;
  totalInvoiced: number;
  totalPaid: number;
  outstanding: number;
  invoiceCount: number;
  lastInvoiceDate?: string | null;
  lastPaymentDate?: string | null;
}

export interface RevenueByCustomerReportResponse {
  from: string;
  to: string;
  grandTotal: number;
  customers: RevenueByCustomerItem[];
}

export interface RevenueByPeriodPoint {
  [key: string]: unknown;
}

export interface RevenueByPeriodReportResponse {
  from?: string;
  to?: string;
  granularity?: string;
  periods?: RevenueByPeriodPoint[];
  [key: string]: unknown;
}

export interface RevenueByProductReportResponse {
  from?: string;
  to?: string;
  grandTotal?: number;
  products?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface AccountsReceivableAgingBucket {
  label: string;
  daysFrom: number;
  daysTo: number;
  amount: number;
  invoiceCount: number;
}

export interface AccountsReceivableTopDebtor {
  customerId: string;
  customerName: string;
  outstanding: number;
  overdue: number;
  overdueInvoiceCount: number;
  oldestDueDate?: string | null;
}

export interface RevenueAccountsReceivableSummaryReportResponse {
  asOf: string;
  totalOutstanding: number;
  outstandingInvoiceCount: number;
  totalOverdue: number;
  overdueInvoiceCount: number;
  agingBuckets: AccountsReceivableAgingBucket[];
  topDebtors: AccountsReceivableTopDebtor[];
}

export type ReportsResponseMap = {
  "production-costs": ProductionCostsReportResponse;
  "input-costs": InputCostsReportResponse;
  "material-usage": MaterialUsageReportResponse;
  "profitability-summary": ProfitabilitySummaryReportResponse;
  "cost-comparison": CostComparisonReportResponse;
  "order-profitability": OrderProfitabilityReportResponse;
  "payment-summary": PaymentSummaryReportResponse;
  "revenue-summary": RevenueSummaryReportResponse;
  "revenue-by-period": RevenueByPeriodReportResponse;
  "revenue-by-customer": RevenueByCustomerReportResponse;
  "revenue-by-product": RevenueByProductReportResponse;
  "revenue-ar-summary": RevenueAccountsReceivableSummaryReportResponse;
};

const buildRevenueParams = (filters: ReportsQueryFilters) => {
  const params: Record<string, string> = {};

  if (filters.asOf) {
    params.asOf = filters.asOf;
  }

  if (filters.startDate) {
    params.from = filters.startDate;
  }

  if (filters.endDate) {
    params.to = filters.endDate;
  }

  if (filters.orderId) {
    params.orderId = filters.orderId;
  }

  if (filters.granularity) {
    params.granularity = filters.granularity;
  }

  return params;
};

const exportRevenueReport = async (
  endpoint: string,
  filters: ReportsQueryFilters,
  fallbackFileName: string
): Promise<ReportExportFile> => {
  const response = await apiClient.get<Blob>(endpoint, {
    params: buildRevenueParams(filters),
    responseType: "blob",
  });

  const fileName =
    parseFileNameFromDisposition(response.headers["content-disposition"]) ||
    fallbackFileName;

  return {
    blob: response.data,
    fileName,
  };
};

const buildQueryParams = (filters: ReportsQueryFilters) => {
  return Object.entries(filters).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );
};

const unwrapResponse = <T>(data: unknown): T => {
  if (data && typeof data === "object" && "data" in data) {
    return (data as { data: T }).data;
  }

  return data as T;
};

const getReport = async <T>(
  endpoint: string,
  filters: ReportsQueryFilters
): Promise<T> => {
  const response = await apiClient.get(endpoint, {
    params: buildQueryParams(filters),
  });

  return unwrapResponse<T>(response.data);
};

const parseFileNameFromDisposition = (contentDisposition?: string) => {
  if (!contentDisposition) {
    return undefined;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1];
};

const exportReport = async (
  endpoint: string,
  filters: ReportsQueryFilters,
  fallbackFileName: string
): Promise<ReportExportFile> => {
  const response = await apiClient.get<Blob>(endpoint, {
    params: buildQueryParams(filters),
    responseType: "blob",
  });

  const fileName =
    parseFileNameFromDisposition(response.headers["content-disposition"]) ||
    fallbackFileName;

  return {
    blob: response.data,
    fileName,
  };
};

export const reportsApi = {
  getProductionCostsReport: (filters: ReportsQueryFilters) =>
    getReport<ProductionCostsReportResponse>(
      "/financial/reports/production-costs",
      filters
    ),

  getInputCostsReport: (filters: ReportsQueryFilters) =>
    getReport<InputCostsReportResponse>(
      "/financial/reports/input-costs",
      filters
    ),

  getMaterialUsageReport: (filters: ReportsQueryFilters) =>
    getReport<MaterialUsageReportResponse>(
      "/financial/reports/material-usage",
      filters
    ),

  getProfitabilitySummaryReport: (filters: ReportsQueryFilters) =>
    getReport<ProfitabilitySummaryReportResponse>(
      "/financial/reports/profitability-summary",
      filters
    ),

  getCostComparisonReport: (filters: ReportsQueryFilters) =>
    getReport<CostComparisonReportResponse>(
      "/financial/reports/cost-comparison",
      filters
    ),

  getOrderProfitabilityReport: (orderId: string) =>
    getReport<OrderProfitabilityReportResponse>(
      `/financial/reports/order-profitability/${orderId}`,
      {}
    ),

  getPaymentSummaryReport: (filters: ReportsQueryFilters) =>
    getReport<PaymentSummaryReportResponse>("/Payments/summary", filters),

  getRevenueSummaryReport: async (
    filters: ReportsQueryFilters
  ): Promise<RevenueSummaryReportResponse> => {
    const response = await apiClient.get("/revenue-reports/summary", {
      params: buildRevenueParams(filters),
    });

    return unwrapResponse<RevenueSummaryReportResponse>(response.data);
  },

  getRevenueByPeriodReport: async (
    filters: ReportsQueryFilters
  ): Promise<RevenueByPeriodReportResponse> => {
    const params: Record<string, string> = {
      ...buildRevenueParams(filters),
      granularity: filters.granularity ?? "month",
    };

    const response = await apiClient.get("/revenue-reports/by-period", {
      params,
    });

    return unwrapResponse<RevenueByPeriodReportResponse>(response.data);
  },

  getRevenueByCustomerReport: async (
    filters: ReportsQueryFilters
  ): Promise<RevenueByCustomerReportResponse> => {
    const response = await apiClient.get("/revenue-reports/by-customer", {
      params: buildRevenueParams(filters),
    });

    return unwrapResponse<RevenueByCustomerReportResponse>(response.data);
  },

  getRevenueByProductReport: async (
    filters: ReportsQueryFilters
  ): Promise<RevenueByProductReportResponse> => {
    const response = await apiClient.get("/revenue-reports/by-product", {
      params: buildRevenueParams(filters),
    });

    return unwrapResponse<RevenueByProductReportResponse>(response.data);
  },

  getRevenueAccountsReceivableSummaryReport: async (
    filters: ReportsQueryFilters
  ): Promise<RevenueAccountsReceivableSummaryReportResponse> => {
    const params: Record<string, string> = {};

    if (filters.asOf) {
      params.asOf = filters.asOf;
    }

    const response = await apiClient.get("/revenue-reports/ar/summary", {
      params,
    });

    return unwrapResponse<RevenueAccountsReceivableSummaryReportResponse>(response.data);
  },

  exportRevenueSummaryReport: (filters: ReportsQueryFilters) =>
    exportRevenueReport(
      "/revenue-reports/summary/export",
      filters,
      "revenue-summary-report.xlsx"
    ),

  exportRevenueSummaryReportPdf: (filters: ReportsQueryFilters) =>
    exportRevenueReport(
      "/revenue-reports/summary/export/pdf",
      filters,
      "revenue-summary-report.pdf"
    ),

  exportRevenueByPeriodReport: (filters: ReportsQueryFilters) =>
    exportRevenueReport(
      "/revenue-reports/by-period/export",
      { ...filters, granularity: filters.granularity ?? "month" },
      "revenue-by-period-report.xlsx"
    ),

  exportRevenueByPeriodReportPdf: (filters: ReportsQueryFilters) =>
    exportRevenueReport(
      "/revenue-reports/by-period/export/pdf",
      { ...filters, granularity: filters.granularity ?? "month" },
      "revenue-by-period-report.pdf"
    ),

  exportRevenueByCustomerReport: (filters: ReportsQueryFilters) =>
    exportRevenueReport(
      "/revenue-reports/by-customer/export",
      filters,
      "revenue-by-customer-report.xlsx"
    ),

  exportRevenueByCustomerReportPdf: (filters: ReportsQueryFilters) =>
    exportRevenueReport(
      "/revenue-reports/by-customer/export/pdf",
      filters,
      "revenue-by-customer-report.pdf"
    ),

  exportRevenueByProductReport: (filters: ReportsQueryFilters) =>
    exportRevenueReport(
      "/revenue-reports/by-product/export",
      filters,
      "revenue-by-product-report.xlsx"
    ),

  exportRevenueByProductReportPdf: (filters: ReportsQueryFilters) =>
    exportRevenueReport(
      "/revenue-reports/by-product/export/pdf",
      filters,
      "revenue-by-product-report.pdf"
    ),

  exportRevenueAccountsReceivableSummaryReport: (filters: ReportsQueryFilters) =>
    exportRevenueReport(
      "/revenue-reports/ar/summary/export",
      filters,
      "accounts-receivable-summary-report.xlsx"
    ),

  exportRevenueAccountsReceivableSummaryReportPdf: (filters: ReportsQueryFilters) =>
    exportRevenueReport(
      "/revenue-reports/ar/summary/export/pdf",
      filters,
      "accounts-receivable-summary-report.pdf"
    ),

  exportProductionCostsReport: (filters: ReportsQueryFilters) =>
    exportReport(
      "/financial/reports/production-costs/export",
      filters,
      "production-costs-report.xlsx"
    ),

  exportProductionCostsReportPdf: (filters: ReportsQueryFilters) =>
    exportReport(
      "/financial/reports/production-costs/export/pdf",
      filters,
      "production-costs-report.pdf"
    ),

  exportInputCostsReport: (filters: ReportsQueryFilters) =>
    exportReport(
      "/financial/reports/input-costs/export",
      filters,
      "input-costs-report.xlsx"
    ),

  exportInputCostsReportPdf: (filters: ReportsQueryFilters) =>
    exportReport(
      "/financial/reports/input-costs/export/pdf",
      filters,
      "input-costs-report.pdf"
    ),

  exportProfitabilitySummaryReport: (filters: ReportsQueryFilters) =>
    exportReport(
      "/financial/reports/profitability-summary/export",
      filters,
      "profitability-summary-report.xlsx"
    ),

  exportProfitabilitySummaryReportPdf: (filters: ReportsQueryFilters) =>
    exportReport(
      "/financial/reports/profitability-summary/export/pdf",
      filters,
      "profitability-summary-report.pdf"
    ),

  exportCostComparisonReport: (filters: ReportsQueryFilters) =>
    exportReport(
      "/financial/reports/cost-comparison/export",
      filters,
      "cost-comparison-report.xlsx"
    ),

  exportCostComparisonReportPdf: (filters: ReportsQueryFilters) =>
    exportReport(
      "/financial/reports/cost-comparison/export/pdf",
      filters,
      "cost-comparison-report.pdf"
    ),

  exportOrderProfitabilityReportPdf: (orderId: string) =>
    exportReport(
      `/financial/reports/order-profitability/${orderId}/export/pdf`,
      {},
      `order-profitability-${orderId}.pdf`
    ),

  exportOrderProfitabilityReport: (orderId: string) =>
    exportReport(
      `/financial/reports/order-profitability/${orderId}/export`,
      {},
      `order-profitability-${orderId}.xlsx`
    ),
};
